/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */

import "./KatexEquationAlterer.css"

import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext"
import * as React from "react"
import { useCallback, useState } from "react"
import { ErrorBoundary } from "react-error-boundary"

import Button from "./Button"
import KatexRenderer from "./KatexRenderer"

export default function KatexEquationAlterer({
  onConfirm,
  initialEquation = ""
}) {
  const [editor] = useLexicalComposerContext()
  const [equation, setEquation] = useState(initialEquation)
  const [inline, setInline] = useState(true)
  const [cursorPosition, setCursorPosition] = useState(0);

  const textareaRef = React.useRef(null);

  const onClick = useCallback(() => {
    onConfirm(equation, inline)
  }, [onConfirm, equation, inline])

  const onCheckboxChange = useCallback(() => {
    setInline(!inline)
  }, [setInline, inline])

  const handleCursorChange = () => {
    const textarea = textareaRef.current;
    if (textarea) {
      setCursorPosition(textarea.selectionStart);
    }
  };

  const addToTextarea = (text) => {
    if(cursorPosition !== null && textareaRef.current) {
      const textarea = textareaRef.current;
      const currentText = textarea.value;
      const newText = currentText.slice(0, cursorPosition) + text + currentText.slice(cursorPosition);
      textarea.value = newText;
      setEquation(newText); // Update the equation state
      setCursorPosition(cursorPosition + text.length); // Move cursor after the inserted text
    }
  }


  return (
    <>
      <div className="KatexEquationAlterer_defaultRow">
        Inline
        <input type="checkbox" checked={inline} onChange={onCheckboxChange} />
      </div>
      {/* mahesh */}
      <div style={{ display: "flex", flexDirection: "row", gap: "10px" }}>
      <button
        type="button"
        className="toolbar-item"
        onClick={() => {addToTextarea("\\alpha")}}
      >
        <i className="format alpha-svgrepo-com" />
      </button>

      <button
        type="button"
        className="toolbar-item"
        onClick={() => {addToTextarea("\\beta")}}
      >
        <i className="format beta-svgrepo-com" />
      </button>

      <button
        type="button"
        className="toolbar-item"
        onClick={() => {addToTextarea("\\gamma")}}
      >
        <i className="format gamma-svgrepo-com" />
      </button>

      <button
        type="button"
        className="toolbar-item"
        onClick={() => {addToTextarea("\\Omega")}}
      >
        <i className="format omega-svgrepo-com" />
      </button>

      <button
        type="button"
        className="toolbar-item"
        onClick={() => {addToTextarea("\\pi")}}
      >
        <i className="format pi-svgrepo-com" />
      </button>
      <button
        type="button"
        className="toolbar-item"
        onClick={() => {addToTextarea("\\mu")}}
      >
        <span>&micro;</span>
      </button>
      <button
        type="button"
        className="toolbar-item"
        onClick={() => {addToTextarea("\\rho")}}
      >
        <span>&rho;</span>
      </button>
      <button
        type="button"
        className="toolbar-item"
        onClick={() => {addToTextarea("\\omega")}}
      >
        <span>&omega;</span>
      </button>
      <button
        type="button"
        className="toolbar-item"
        onClick={() => {addToTextarea("\\theta")}}
      >
        <span>&theta;</span>
      </button>
      <button
        type="button"
        className="toolbar-item"
        onClick={() => {addToTextarea("\\Delta")}}
      >
        <span>&Delta;</span>
      </button>
      <button
        type="button"
        className="toolbar-item"
        onClick={() => {addToTextarea("\\Phi")}}
      >
        <span>&Phi;</span>
      </button>
        <button
          type="button"
          className="toolbar-item"
          onClick={() => {addToTextarea("\\pm")}}
        >
          <span>&plusmn;</span>
        </button>
        <button
          type="button"
          className="toolbar-item"
          onClick={() => {addToTextarea("\\degree C")}}
        >
          <span>&deg;</span>
        </button>
      </div>
      <div style={{ display: "flex", flexDirection: "row", gap: "10px" }}>
      <button
        type="button"
        className="toolbar-item"
        onClick={() => {addToTextarea("\\sqrt{}")}}
      >
        <i className="format square-root" />
      </button>

      <button
        type="button"
        className="toolbar-item"
        onClick={() => {addToTextarea("{}^{}")}}
      >
        <i className="format superscript-svgrepo-com" />
      </button>
      <button
        type="button"
        className="toolbar-item"
        onClick={() => {addToTextarea("\\frac{}{}")}}
      >
        <i className="format divide-svgrepo-com" />
      </button>

      <button
        type="button"
        className="toolbar-item"
        onClick={() => {addToTextarea("{}_{}")}}
      >
        <i className="format subscript-svgrepo-com" />
      </button>

      <button
        type="button"
        className="toolbar-item"
        onClick={() => {addToTextarea("\\times")}}
      >
        X
      </button>
      <button
        type="button"
        className="toolbar-item"
        onClick={() => {addToTextarea("\\,")}}
      >
        s
      </button>
      <button
        type="button"
        className="toolbar-item"
        onClick={() => {addToTextarea("\\left(")}}
      >
        (
      </button>
      <button
        type="button"
        className="toolbar-item"
        onClick={() => {addToTextarea("\\right)")}}
      >
        )
      </button>
      <button
        type="button"
        className="toolbar-item"
        onClick={() => {addToTextarea("\\overline{}")}}
      >
        <span>&macr;</span>
      </button>
      <button
        type="button"
        className="toolbar-item"
        onClick={() => {addToTextarea("{}^{}_{}\\mathrm{B}^{}_{}")}}
      >
        ch
      </button>
     
      
      </div>

      <div className="KatexEquationAlterer_defaultRow">Equation </div>
      <div className="KatexEquationAlterer_centerRow">
        {inline ? (
          <input
            onChange={event => {
              setEquation(event.target.value)
            }}
            value={equation}
            className="KatexEquationAlterer_textArea"
            onClick={handleCursorChange}
            onKeyUp={handleCursorChange}
            onSelect={handleCursorChange}
            ref={textareaRef}
          />
        ) : (
          <textarea
            onChange={event => {
              setEquation(event.target.value)
            }}
            value={equation}
            className="KatexEquationAlterer_textArea"
          />
        )}
      </div>
      <div className="KatexEquationAlterer_defaultRow">Visualization </div>
      <div className="KatexEquationAlterer_centerRow">
        <ErrorBoundary onError={e => editor._onError(e)} fallback={null}>
          <KatexRenderer
            equation={equation}
            inline={false}
            onDoubleClick={() => null}
          />
        </ErrorBoundary>
      </div>
      <div className="KatexEquationAlterer_dialogActions">
        <Button onClick={onClick}>Confirm</Button>
      </div>
    </>
  )
}
